import { LogLevel } from '@instinet-analytics/core-lib';
import { MessageData } from '@instinet-analytics/core-lib';
import { addItemToArrayWithMaxSize } from 'utils/arrays/arrayUtils';

const MAX_LOGS_PER_LEVEL = 500;

export interface LogMessagesLogItem {
    timestamp: Date;
    level: LogLevel;
    source: string;
    message: string;
    data?: MessageData;
}

export interface LogMessagesLevelLog {
    firstTimestamp?: Date;
    lastTimestamp?: Date;
    count: number;
    items: LogMessagesLogItem[];
}

export interface LogMessagesLog {
    debug: LogMessagesLevelLog;
    info: LogMessagesLevelLog;
    warning: LogMessagesLevelLog;
    critical: LogMessagesLevelLog;
    firstTimestamp?: Date;
    lastTimestamp?: Date;
    count: number;
}

const initialLogMessagesLevelLog: LogMessagesLevelLog = {
    count: 0,
    items: [],
};

export const buildInitialLogMessagesLog = () => ({
    count: 0,
    debug: { ...initialLogMessagesLevelLog },
    info: { ...initialLogMessagesLevelLog },
    warning: { ...initialLogMessagesLevelLog },
    critical: { ...initialLogMessagesLevelLog },
});

/*
 * In memory store for messages that avoids each log message having to pass through the Redux middleware
 */
export class LogMessagesStoreService {
    public logs: LogMessagesLog = buildInitialLogMessagesLog();

    public log(
        level: LogLevel,
        source: string,
        message: string,
        data?: MessageData
    ) {
        const timestamp = new Date();
        this.increment(this.logs, timestamp);

        const messageLog = this.getLog(level);
        this.increment(messageLog, timestamp);

        const item = { timestamp, level, source, message, data };
        messageLog.items = addItemToArrayWithMaxSize(
            item,
            messageLog.items,
            MAX_LOGS_PER_LEVEL
        );
    }

    private getLog(level: LogLevel): LogMessagesLevelLog {
        switch (level) {
            case LogLevel.Critical:
                return this.logs.critical;
            case LogLevel.Warning:
                return this.logs.warning;
            case LogLevel.Info:
                return this.logs.info;
            default:
                return this.logs.debug;
        }
    }

    private increment(
        timestamps: {
            firstTimestamp?: Date;
            lastTimestamp?: Date;
            count: number;
        },
        timestamp: Date
    ) {
        if (!timestamps.firstTimestamp) {
            timestamps.firstTimestamp = timestamp;
        }
        timestamps.lastTimestamp = timestamp;
        timestamps.count++;
    }
}

export function extractLogMessagesFromLogs(
    logs?: LogMessagesLog
): LogMessagesLogItem[] {
    if (!logs) {
        return [];
    }

    return [
        ...logs.debug.items,
        ...logs.info.items,
        ...logs.warning.items,
        ...logs.critical.items,
    ];
}
